import React from "react";
import { Crown, Check, Mail, Download } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/AppContext";
import api from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function MembershipModal({ open, onOpenChange }) {
  const { t, user, refreshUser } = useApp();
  const [invoices, setInvoices] = React.useState([]);

  React.useEffect(() => {
    if (open && user?.membership === "pro") {
      api.get("/membership/my-invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
    }
  }, [open, user]);

  const upgrade = async () => {
    try {
      await api.post("/membership/upgrade");
      toast.success(t("request_sent"));
      onOpenChange(false);
    } catch {
      toast.error("Error");
    }
  };

  const basicFeatures = [t("basic_desc")];
  const proFeatures = [t("pro_desc"), t("sell_parts"), "Priority support"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-cyan-500/30 max-w-2xl" data-testid="membership-modal">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl text-slate-900">{t("membership")}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <div className="ps-glow-card rounded-xl p-6" data-testid="plan-basic">
            <h3 className="font-head text-lg font-bold text-slate-700">{t("basic_membership")}</h3>
            <p className="font-display text-3xl font-black text-slate-900 mt-2">$0</p>
            <ul className="mt-4 space-y-2">
              {basicFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-slate-500" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-slate-500 font-mono-ps">
              {user?.membership === "basic" ? "✓ Current plan" : ""}
            </div>
          </div>

          <div className="ps-glow-card rounded-xl p-6 border-amber-500/40" data-testid="plan-pro">
            <div className="flex items-center gap-2">
              <Crown className="text-amber-500" size={20} />
              <h3 className="font-head text-lg font-bold text-amber-500">{t("pro_membership")}</h3>
            </div>
            <p className="font-display text-3xl font-black text-slate-900 mt-2">$3000<span className="text-sm text-slate-500 font-normal">/yr</span></p>
            <ul className="mt-4 space-y-2">
              {proFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                  <Check size={16} className="text-emerald-600" /> {f}
                </li>
              ))}
            </ul>
            {user?.membership === "pro" ? (
              <div className="mt-6">
                <p className="text-sm text-emerald-600 font-mono-ps">✓ Active</p>
                {user?.membership_expires_at && (
                  <p className="text-xs text-slate-500 mt-1 font-mono-ps">{t("valid_until")}: {user.membership_expires_at.slice(0, 10)}</p>
                )}
              </div>
            ) : (
              <>
                <Button
                  data-testid="membership-upgrade-btn"
                  onClick={upgrade}
                  className="mt-6 w-full bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-head font-bold"
                >
                  {t("request_membership")}
                </Button>
                <p className="mt-2 text-[11px] text-slate-500 text-center">{t("activation_note")}</p>
              </>
            )}
          </div>
        </div>
        {invoices.length > 0 && (
          <div className="mt-4 border-t border-slate-200 pt-4" data-testid="invoice-archive">
            <h4 className="font-head text-sm font-bold text-slate-700 mb-2">{t("invoice_archive")}</h4>
            <ul className="space-y-1.5">
              {invoices.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-mono-ps text-slate-600 truncate">{inv.invoice_no} · {(inv.activated_at || "").slice(0, 10)}</span>
                  <a
                    data-testid={`invoice-download-${inv.id}`}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/membership/invoice/${inv.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 flex items-center gap-1 text-cyan-600 hover:underline font-head font-bold"
                  >
                    <Download size={13} /> {t("download_invoice")}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="flex items-center gap-2 justify-center text-xs text-slate-500 font-mono-ps mt-4">
          <Mail size={13} /> {t("contact_us")}
        </p>
      </DialogContent>
    </Dialog>
  );
}
